import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Calculator, Camera, Users, BotIcon, ClipboardCheck, Briefcase } from 'lucide-react';

interface OnboardingModalProps {
  onClose: () => void;
}

export function OnboardingModal({ onClose }: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.2,
        duration: 0.5 
      }
    },
    exit: { 
      opacity: 0, 
      transition: { duration: 0.3 } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };
  
  const features = [
    {
      title: "Bienvenue sur pvtistes.net",
      description: "L'application qui vous accompagne à chaque étape de votre PVT pour une expérience sans stress et réussie.",
      icon: <ClipboardCheck className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-primary-500 to-accent-500",
      image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2070&auto=format&fit=crop"
    },
    {
      title: "Simulez votre budget en quelques clics",
      description: "Planifiez vos dépenses et économies avec un outil simple adapté à votre PVT.",
      icon: <Calculator className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-primary-500 to-primary-600",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2671&auto=format&fit=crop"
    },
    {
      title: "Partagez vos meilleurs moments",
      description: "Immortalisez et partagez vos aventures en photos et vidéos avec la communauté.",
      icon: <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-accent-500 to-accent-600",
      image: "https://images.unsplash.com/photo-1512100356356-de1b84283e18?q=80&w=1974&auto=format&fit=crop"
    },
    {
      title: "Découvrez des offres d'emploi",
      description: "Trouvez des opportunités professionnelles adaptées aux Pvtistes et boostez votre expérience.",
      icon: <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-emerald-500 to-emerald-600",
      image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2574&auto=format&fit=crop"
    },
    {
      title: "Connectez-vous avec d'autres Pvtistes",
      description: "Trouvez des compagnons de voyage et échangez avec la communauté.",
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-emerald-500 to-emerald-600",
      image: "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?q=80&w=2670&auto=format&fit=crop"
    },
    {
      title: "Votre assistant IA personnel",
      description: "Posez toutes vos questions pour obtenir des réponses instantanées et personnalisées.",
      icon: <BotIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
      color: "from-purple-500 to-purple-600",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop"
    }
  ];

  useEffect(() => {
    // Désactiver le défilement du corps lorsque le modal est ouvert
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Gestion des événements tactiles pour le swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentStep < features.length - 1) {
      nextStep();
    } else if (isRightSwipe && currentStep > 0) {
      prevStep();
    }
  };

  const nextStep = () => {
    if (currentStep < features.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    setIsClosing(true);
    // Enregistrer que l'utilisateur a vu l'onboarding
    localStorage.setItem('hasSeenOnboarding', 'true');
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div 
      className={`fixed inset-0 z-50 bg-white transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <AnimatePresence mode="wait">
        <motion.div 
          key={currentStep}
          className="absolute inset-0 flex flex-col"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
        >
          {/* Image de fond avec overlay */}
          <div className="absolute inset-0 z-0">
            <img 
              src={features[currentStep].image}
              alt={features[currentStep].title}
              className="w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/90 to-white/95" />
          </div>

          {/* Logo */}
          <div className="relative z-10 mx-auto pt-8 sm:pt-12 lg:pt-20">
            <img 
              src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_positive_fullcolor.png"
              alt="pvtistes.net"
              className="h-10 sm:h-12 md:h-16 w-auto"
            />
          </div>

          {/* Contenu principal */}
          <motion.div 
            className="relative z-10 flex-1 flex flex-col justify-center items-center max-w-3xl mx-auto px-4 sm:px-6 text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              variants={itemVariants}
              className="mb-4 sm:mb-6"
            >
              <div className={`inline-flex items-center justify-center p-3 sm:p-4 rounded-full bg-gradient-to-r ${features[currentStep].color} shadow-xl mb-4 sm:mb-6`}>
                {features[currentStep].icon}
              </div>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-2"
            >
              {features[currentStep].title}
            </motion.h1>

            <motion.p 
              variants={itemVariants}
              className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-12 max-w-lg px-2"
            >
              {features[currentStep].description}
            </motion.p>
          </motion.div>

          {/* Navigation et indicateurs */}
          <div className="relative z-10 w-full pb-8 sm:pb-16 px-4 sm:px-6">
            {/* Indicateurs de progression */}
            <div className="flex justify-center items-center gap-2 mb-6 sm:mb-8">
              {features.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all duration-300 ${
                    currentStep === index 
                      ? 'bg-primary-500 scale-125' 
                      : 'bg-gray-300'
                  }`}
                  aria-label={`Étape ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex justify-between items-center">
              {currentStep > 0 ? (
                <button
                  onClick={prevStep}
                  className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Précédent"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              ) : (
                <div className="w-10 sm:w-12">{/* Espace vide pour maintenir l'alignement */}</div>
              )}

              {currentStep < features.length - 1 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                >
                  <span className="text-xs sm:text-sm font-medium">Swipez pour découvrir</span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  className="flex items-center gap-2 px-5 py-3 sm:px-8 sm:py-4 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white text-sm sm:text-base font-medium transition-all duration-300 transform hover:scale-105 shadow-xl"
                >
                  <span>Commencer l'aventure</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {currentStep < features.length - 1 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Suivant"
                >
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              ) : (
                <div className="w-10 sm:w-12">{/* Espace vide pour maintenir l'alignement */}</div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}